import {
    isSpringWebSocketCloseStatus,
    SockJsStreamingServiceErrorCodes,
    WellKnownWebsocketErrorCodes,
} from 'communication/streaming/SockJsStreamingErrorCodes';

export interface StreamingErrorText {
    title: string;
    newportInstructions: string;
}

export const newportInstructions = (reloadPossible: boolean) => ({
    pleaseRefreshBySelecting: `Please refresh by selecting a new order or portfolio${
        reloadPossible ? ', or reload by clicking the button below' : ''
    }.`,
    pleaseContactSupport: 'Please contact support using the button above.',
    pleaseCloseAndRelaunch: `Please close the Indigo window and relaunch${
        reloadPossible ? ', or reload by clicking the button below' : ''
    }. If errors persist please contact support using the button above.`,
    pleaseWaitAFewMinsThenRefresh: `Please wait a few minutes and then refresh by selecting a new order or portfolio${
        reloadPossible ? ', or reload by clicking the button below' : ''
    }.`,
});

export const streamingErrors = (reloadPossible: boolean) => ({
    lostConnection: {
        title: "You've been logged out due to inactivity.",
        newportInstructions:
            newportInstructions(reloadPossible).pleaseRefreshBySelecting,
    },
    indigo1ServerRestart: {
        title: 'The Indigo server is restarting.',
        newportInstructions:
            newportInstructions(reloadPossible).pleaseWaitAFewMinsThenRefresh,
    },
    webSocketInitError: {
        title: 'We have experienced an unknown connection error.',
        newportInstructions:
            newportInstructions(reloadPossible).pleaseContactSupport,
    },
    sessionAuthError: {
        title: 'Connection authentication error.',
        newportInstructions:
            newportInstructions(reloadPossible).pleaseContactSupport,
    },
    indigo1StreamInitError: {
        title: 'Error connecting to back end services.',
        newportInstructions:
            newportInstructions(reloadPossible).pleaseWaitAFewMinsThenRefresh,
    },
    indigo1StreamLost: {
        title: 'Indigo1 stream lost.',
        newportInstructions:
            newportInstructions(reloadPossible).pleaseWaitAFewMinsThenRefresh,
    },
    springWebSocket: {
        title: 'Unexpected Websocket error (ERRORCODE).',
        newportInstructions:
            newportInstructions(reloadPossible).pleaseCloseAndRelaunch,
    },
    other: {
        title: 'Websocket connection error.',
        newportInstructions:
            newportInstructions(reloadPossible).pleaseCloseAndRelaunch,
    },
});
function springWebsocketError(errorCode: number, reloadPossible = false) {
    return {
        title: `Unexpected Websocket error (${errorCode}).`,
        newportInstructions: `Please close the Indigo window and relaunch. If errors persist please contact support using the button above${
            reloadPossible ? ', or reload by clicking the button below' : ''
        }.`,
    };
}

export function buildFromWebsocketErrorCode(
    errorCode?: number,
    reloadPossible = false
): StreamingErrorText {
    switch (errorCode) {
        case SockJsStreamingServiceErrorCodes.Indigo1SessionTimeoutOrInvalidation:
            return streamingErrors(reloadPossible).lostConnection;
        case SockJsStreamingServiceErrorCodes.Indigo1ServerShuttingDown:
            return streamingErrors(reloadPossible).indigo1ServerRestart;
        case WellKnownWebsocketErrorCodes.InitError:
            return streamingErrors(reloadPossible).webSocketInitError;
        case WellKnownWebsocketErrorCodes.SessionAuthError:
            return streamingErrors(reloadPossible).sessionAuthError;
        case WellKnownWebsocketErrorCodes.Indigo1StreamInitError:
            return streamingErrors(reloadPossible).indigo1StreamInitError;
        case WellKnownWebsocketErrorCodes.Indigo1StreamLost:
            return streamingErrors(reloadPossible).indigo1StreamLost;
        case WellKnownWebsocketErrorCodes.InvalidIndigo2Request:
        case WellKnownWebsocketErrorCodes.Indigo1RequestError:
        case WellKnownWebsocketErrorCodes.Indigo1StreamError:
        case WellKnownWebsocketErrorCodes.UnhandledError:
        case WellKnownWebsocketErrorCodes.UnsupportedOperation:
            return {
                title: `Error processing stream request (${errorCode}).`,
                newportInstructions: '',
            };
    }

    if (isSpringWebSocketCloseStatus(errorCode)) {
        return springWebsocketError(errorCode!, reloadPossible);
    }

    return streamingErrors(reloadPossible).other;
}
